import { BuildingIcon } from '@/components/atoms/icons';
import { BloomCmsIcon } from '@/modules/bloom-universal/components/BloomCmsIcon';
import React, { useMemo } from 'react';
import { PixelRatio, Platform } from 'react-native';

export interface WebAmenityIconProps {
  readonly iconUrl: string | null;
  readonly label: string;
  readonly size: number;
  readonly defaultColor: string;
}

/**
 * Native only: ask Contentful's Images API for a small PNG instead of the raw
 * SVG. Amenity icons are all SVGs and some are huge (Clinic.svg is ~2.1 MB for
 * a 40×40 glyph) — iOS decodes SVG on the main thread, which froze the
 * property details screen until the icons finished. The PNG is ~600 bytes.
 * Web keeps the original SVG URL (browser renders it off the JS thread).
 */
function toNativeIconUrl(url: string | null, size: number): string | null {
  if (!url || Platform.OS === 'web' || !url.includes('ctfassets.net')) {
    return url;
  }
  const px = Math.round(size * PixelRatio.get());
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}fm=png&w=${px}&h=${px}&fit=pad`;
}

/**
 * Amenity icon backed by Contentful, with one local default for unknown
 * amenities and missing/unresolved Contentful assets.
 */
export function WebAmenityIcon({
  iconUrl,
  label,
  size,
  defaultColor,
}: Readonly<WebAmenityIconProps>) {
  const resolvedUrl = useMemo(() => toNativeIconUrl(iconUrl, size), [iconUrl, size]);
  return (
    <BloomCmsIcon
      url={resolvedUrl ?? undefined}
      size={size}
      accessibilityLabel={label}
      fallback={<BuildingIcon size={size} color={defaultColor} />}
    />
  );
}
